import type { ReactNode } from "react";

import { screenById } from "../navigation.ts";
import { Hero } from "./Hero.tsx";
import { PhoneShell, Rows } from "./PhoneShell.tsx";

/**
 * What a designed screen that is not built says for itself.
 *
 * Eight of the thirty-two rider screens are not drawn by this build, and every one of them is
 * **blocked**: `Checkout`, the two multi-stop screens, and the five money and account screens whose
 * subjects are published only under `/internal/v1` or not at all. Each says what it is waiting on,
 * which is the one useful thing an application can say about a screen it cannot draw and is
 * considerably more useful than a blank.
 *
 * The second branch below — a screen with an empty `waiting` list — is the shape this component was
 * written for and no screen is in any more: a screen that **is** buildable and simply has not been
 * built in this tranche yet. It is kept rather than deleted because the list has grown in three
 * tranches so far and will again, and because a buildable screen reached by address must not be told
 * that the platform publishes no source for it. `5 Finding` and `5b No driver` were in it for an
 * hour between ADR 0031 serving DSP-04's read and this application routing them.
 *
 * The list is in `navigation.ts` beside the operation each built screen reads, and
 * `tests/navigation.test.ts` checks it against `tests/contract/mobile-screen-support.test.ts`'s own
 * measurement so the two cannot drift.
 */
export function ScreenOutlook({ pageId }: { readonly pageId: string }): ReactNode {
  const screen = screenById(pageId);

  if (screen === undefined) {
    return (
      <PhoneShell
        screen="journey screen--state"
        hero={<Hero kind="none" />}
        chip={{ class: "closed", label: "no such screen" }}
        title="There's no screen here"
        sub="That address doesn't name a screen of this app."
        actions={[]}
        flag="wording not in the design"
      />
    );
  }

  const waiting = screen.waiting;

  return (
    <PhoneShell
      screen="journey screen--state"
      hero={<Hero kind="none" />}
      chip={{ class: "idle", label: "not built" }}
      title={screen.title}
      sub={
        waiting.length === 0
          ? `Designed in "${screen.design}". It is buildable and is in the second tranche of this application${screen.operation === null ? "" : `, over ${screen.operation}`}.`
          : `Designed in "${screen.design}". It isn't built because the platform publishes no source for what it draws.`
      }
      flag="wording not in the design"
      actions={[]}
    >
      <Rows
        rows={waiting.map((item) => ({
          key: item.element,
          value: null,
          because: `needs ${item.needs}`,
        }))}
      />
    </PhoneShell>
  );
}
